/**
 * src/ui/components/common/OfflineBanner.tsx
 *
 * F 452 — THE BANNER THAT SAYS THE APP IS SHOWING CACHED DATA.
 *
 * THE TEXT IS THE DISPATCH'S, VERBATIM: "You are offline. Showing cached
 * data." A banner that paraphrases the state is a banner the operator has to
 * interpret, and the one thing this banner must not be is ambiguous.
 *
 * WHEN IT SHOWS. Two independent facts, because they fail differently:
 *
 *   - the browser says it is offline (`offline` / `online` window events), or
 *   - a read of the graph failed (`graph:unreachable`).
 *
 * A successful read clears the second fact but not the first: the graph
 * answering proves the network works, and the browser's own flag is the only
 * authority on whether the DEVICE is connected.
 *
 * WHY IT IS NOT A TOAST. F 490's toast is mounted at the app's root, outside
 * the scroll container, "because a toast that scrolls with the page is a
 * toast that gets missed". The same argument applies here with more force:
 * this is not a message, it is a standing condition. It stays until the
 * condition is over, and it is announced politely so a screen reader says it
 * too.
 */
import * as React from 'react';
import { eventBus } from '../../../core/event-bus';

/** The dispatch's wording. Exported so a test can assert the exact string. */
export const OFFLINE_BANNER_TEXT = 'You are offline. Showing cached data.';

export function OfflineBanner(): React.ReactElement | null {
  // F 452 — THE INITIAL STATE IS READ, NOT ASSUMED.
  //
  // A page that is LOADED while the network is already gone never sees the
  // browser's `offline` event: the event fired before this component
  // mounted. So the banner has to start from `navigator.onLine` — otherwise
  // the operator reloads the app offline, the app boots, the graph read
  // fails, and the banner stays hidden while the screen shows cached data.
  // (That is exactly what the live proof caught.)
  const [browserOffline, setBrowserOffline] = React.useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false,
  );
  const [graphUnreachable, setGraphUnreachable] = React.useState(false);

  React.useEffect(() => {
    const onUnreachable = () => setGraphUnreachable(true);
    const onReachable = () => setGraphUnreachable(false);
    eventBus.on('graph:unreachable', onUnreachable);
    eventBus.on('graph:reachable', onReachable);

    const onWindowOffline = () => setBrowserOffline(true);
    const onWindowOnline = () => setBrowserOffline(false);
    window.addEventListener('offline', onWindowOffline);
    window.addEventListener('online', onWindowOnline);

    return () => {
      eventBus.off('graph:unreachable', onUnreachable);
      eventBus.off('graph:reachable', onReachable);
      window.removeEventListener('offline', onWindowOffline);
      window.removeEventListener('online', onWindowOnline);
    };
  }, []);

  if (!browserOffline && !graphUnreachable) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="fc-offline-banner"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        padding: '8px 16px',
        textAlign: 'center',
        fontSize: 13,
        fontWeight: 500,
        color: '#1c1917',
        background: '#fcd34d',
        borderBottom: '1px solid #f59e0b',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
      }}
    >
      {OFFLINE_BANNER_TEXT}
    </div>
  );
}

export default OfflineBanner;
